import React from 'react';
import HubPage from '../components/HubPage';
import {
  Activity, Pill, Package, Users, Megaphone, Wallet, Receipt, FileText,
  TrendingUp, Clock, Calendar, Heart, Zap, Bone, BookOpen, Image, Camera,
  ShoppingCart, Truck, Boxes, ArrowRightLeft, Building2, Briefcase, Award,
  GraduationCap, DollarSign, CreditCard, Ticket, Repeat, BarChart3, MessageSquare,
  Send, PhoneCall, Bot, Filter, Link2, Key, Eye, UserPlus, ClipboardList,
  CheckSquare, UserCheck, Stethoscope, Layers, Percent,
} from 'lucide-react';

// Card arrays are exported so App.js can derive a key→label map for the
// header breadcrumb without hard-coding it twice.

// CLINICAL hub now holds CROSS-PATIENT operations only. Per-patient clinical
// data (Prescriptions, Dental Chart, Imaging, Vital Signs, Ortho, Perio,
// Implants, Education, Referrals, Plans) lives inside Patient Profile tabs.
export const CLINICAL_CARDS = [
  // Clinical Suite — for cross-patient view (operations dashboard); per-patient
  // version is inside Patient Profile.
  { key:'clinical',           icon: Stethoscope,   label:'Clinical Suite',     desc:'Cross-patient view — Ortho · Implants · Perio · Plans', color:'#0EA5E9' },
  { key:'clinical-protocols', icon: ClipboardList, label:'Clinical Protocols', desc:'Clinic-wide treatment protocols',                    color:'#84CC16' },
  { key:'treatment-outcomes', icon: BarChart3,     label:'Treatment Outcomes', desc:'Success rates · clinic-wide stats',                  color:'#0D9488' },
  { key:'literature-search',  icon: BookOpen,      label:'Literature Search',  desc:'Evidence-based research',                            color:'#3B82F6' },
  { key:'lab',                icon: Package,       label:'Lab Work Queue',     desc:'All clinic crown/bridge/dispatch',                   color:'#A855F7' },
  // Patient lifecycle / recall workflows.
  { key:'recalls',            icon: Calendar,      label:'Recalls',            desc:'Recare scheduling + AI Auto campaigns',              color:'#0EA5E9' },
  { key:'patient-referrals',  icon: Link2,         label:'Patient Referrals',  desc:'Patient referral program',                           color:'#EC4899' },
];

export const BILLING_CARDS = [
  { key:'billing',         icon: Receipt,     label:'Billing',          desc:'Bills, invoices, proformas', color:'#4F46E5' },
  { key:'estimates',       icon: FileText,    label:'Estimates',        desc:'Treatment quotes & approval', color:'#8B5CF6' },
  { key:'packages',        icon: Layers,      label:'Treatment Packages',desc:'Bundled service plans',      color:'#7C3AED' },
  // insurance-verify merged into Insurance as "Pre-Auth Verify" tab.
  { key:'insurance',       icon: FileText,    label:'Insurance',        desc:'Claims · Companies · Pre-Auth Verify', color:'#0EA5E9' },
  { key:'financial',       icon: Wallet,      label:'Financial Controls',desc:'Ageing, write-offs, audit', color:'#10B981' },
  { key:'ledger',          icon: FileText,    label:'Patient Ledger',   desc:'Transaction history',          color:'#F59E0B' },
  { key:'credit-notes',    icon: CreditCard,  label:'Credit Notes',     desc:'Refunds & adjustments',        color:'#EF4444' },
  { key:'coupons',         icon: Ticket,      label:'Coupons',          desc:'Discount codes',               color:'#EC4899' },
  { key:'recurring-charges', icon: Repeat,    label:'Recurring Charges',desc:'Subscriptions, plans',         color:'#6366F1' },
  { key:'gift-cards',      icon: Ticket,      label:'Gift Cards',       desc:'Issue & redeem gift cards',    color:'#F97316' },
  // analyzer merged into Expenses as a tab.
  { key:'expenses',        icon: Wallet,      label:'Expenses',         desc:'Entry · P&L · Categories · Analyzer', color:'#DC2626' },
  { key:'tax',             icon: Percent,     label:'Tax Compliance',   desc:'GST returns, HSN codes',       color:'#84CC16' },
  { key:'day-end',         icon: Calendar,    label:'Day End',          desc:'Daily closing, cash',          color:'#0D9488' },
  { key:'doctor-commissions', icon: DollarSign, label:'Doctor Commissions', desc:'Commission calculations', color:'#EAB308' },
  // Phase 2 / 4 additions — financial + government schemes.
  { key:'emi-plans',       icon: CreditCard,  label:'EMI Plans',       desc:'Treatment installments + UPI Autopay',     color:'#7C3AED' },
  { key:'profitability',   icon: TrendingUp,  label:'Profitability',   desc:'Revenue + margin per treatment type',     color:'#0D9488' },
  { key:'gov-schemes',     icon: Award,       label:'Govt Schemes',    desc:'PMJAY · CGHS · RGHS empanelment + claims', color:'#7C2D12' },
  // dynamic-pricing and saas-billing removed — admin/strategy concerns, not
  // daily billing. Still accessible via direct URL or Admin hub.
];

// Simplified to 2 cards. The Pharmacy card opens a single workspace with
// 3 internal tabs: Selling, Inventory, Reports. Legacy keys (pharmacy-smart,
// pharmacy-advanced, purchase-orders, grns, stock-adjustments, stock-transfers,
// pharmacy-suppliers, supplier-scorecard, supplier-analytics, inventory-forecast)
// still resolve via App.js to PharmacyPage with the appropriate initialTab.
// `lab` moved to CLINICAL_CARDS.
export const PHARMACY_CARDS = [
  { key:'pharmacy',  icon: Pill,    label:'Pharmacy',         desc:'Selling · Inventory · Reports', color:'#4F46E5' },
  { key:'inventory', icon: Package, label:'Clinic Inventory', desc:'Non-medicine consumables',      color:'#8B5CF6' },
];

export const STAFF_CARDS = [
  { key:'staff-attendance', icon: Clock,      label:'Attendance',       desc:'Biometric & PWA check-in', color:'#4F46E5' },
  { key:'staff-checkin',   icon: UserCheck,   label:'Check-In Kiosk',   desc:'PWA clock-in for staff',  color:'#8B5CF6' },
  { key:'staff',           icon: Users,       label:'Staff Directory',  desc:'Employee list & profiles', color:'#0EA5E9' },
  { key:'hr-masters',      icon: Briefcase,   label:'HR Masters',       desc:'Designations, departments', color:'#06B6D4' },
  // 4 cards (leaves/payslips/incentives/advances) consolidated into one Payroll card.
  { key:'payroll',         icon: Wallet,      label:'Payroll',          desc:'Leaves · Payslips · Incentives · Advances', color:'#10B981' },
  // certificates + staff-training merged → Credentials hub
  { key:'credentials',     icon: Award,       label:'Credentials',      desc:'Certificates + Training records', color:'#F97316' },
  // staff-tasks + staff-onboarding merged → Checklists hub
  { key:'checklists',      icon: CheckSquare, label:'Checklists',       desc:'Daily tasks + Onboarding',  color:'#0D9488' },
];

export const MARKETING_CARDS = [
  // 11 WhatsApp cards consolidated into one — opens WhatsAppHubPage with
  // 11 internal tabs (Cloud · Inbox · Contacts · Templates · Campaigns ·
  // Auto-Reply · Flows · Forms · Interactive · Analytics · API Keys).
  { key:'whatsapp',        icon: MessageSquare, label:'WhatsApp',       desc:'Inbox · Templates · Campaigns · Flows · Analytics', color:'#16A34A' },
  { key:'marketing',       icon: Megaphone,   label:'Marketing Engine', desc:'Campaigns, offers',        color:'#F59E0B' },
  { key:'leads',           icon: UserPlus,    label:'Leads Pipeline',   desc:'Sales lead tracking',      color:'#EAB308' },
  // bookings moved here from Clinical — public booking queue + wait-list (tab inside).
  { key:'bookings',        icon: Calendar,    label:'Online Bookings',  desc:'Public web bookings + wait list',  color:'#06B6D4' },
  { key:'reviews',         icon: Eye,         label:'Reviews',          desc:'Google review automation', color:'#8B5CF6' },
  // Telephony consolidated — 6 separate pages (call-log, tracked-numbers,
  // calling-mode, voip, fax, frontdesk-inbox) merged into one TelephonyHubPage.
  { key:'telephony',       icon: PhoneCall,   label:'Telephony',        desc:'Call Log · Tracking · VoIP · Fax · AI Front Desk', color:'#F97316' },
  // recalls / recare-campaigns / patient-referrals / bookings moved to
  // CLINICAL_CARDS — they are patient-facing lifecycle workflows.
];

export const ClinicalHubPage = ({ onNavigate }) => (
  <HubPage title="Clinical" subtitle="Treatments, prescriptions, specialty records & patient education"
    onNavigate={onNavigate} cards={CLINICAL_CARDS} />
);

export const BillingHubPage = ({ onNavigate }) => (
  <HubPage title="Billing" subtitle="Invoices, payments, estimates, insurance & financial records"
    onNavigate={onNavigate} cards={BILLING_CARDS} />
);

export const PharmacyHubPage = ({ onNavigate }) => (
  <HubPage title="Pharmacy & Inventory" subtitle="Stock, orders, suppliers & pharmacy retail operations"
    onNavigate={onNavigate} cards={PHARMACY_CARDS} />
);

export const StaffHubPage = ({ onNavigate }) => (
  <HubPage title="Staff Management" subtitle="HR, attendance, payroll, training & staff operations"
    onNavigate={onNavigate} cards={STAFF_CARDS} />
);

export const MarketingHubPage = ({ onNavigate }) => (
  <HubPage title="Marketing & Engagement" subtitle="WhatsApp, campaigns, leads, reviews & patient retention"
    onNavigate={onNavigate} cards={MARKETING_CARDS} />
);
